import React, { useRef, useState, useEffect, useCallback, memo } from "react";
import { Button, IconButton, Input } from './ui';
import { X } from 'lucide-react';
import { useSearchStore } from "@/stores/searchStore";
import { useNavigate } from "react-router-dom";
import SearchAutocomplete from "./SearchAutocomplete";
import type { SearchAutocompleteHandle } from "./SearchAutocomplete";
import type { TypeaheadHit } from "../services/algoliaSearch";
import { useMapActions } from "../hooks/useMapActions";

interface SearchBarProps {
  onSearch?: (val: string) => void;
  className?: string;
}

const SearchBarComponent: React.FC<SearchBarProps> = ({ onSearch, className }) => {
  const storeQuery = useSearchStore(s => s.query);
  const setQuery = useSearchStore(s => s.setQuery);
  const runSearch = useSearchStore(s => s.runSearch);
  const runSearchWithQuery = useSearchStore(s => s.runSearchWithQuery);
  const resetSearch = useSearchStore(s => s.resetSearch);
  const viewMode = useSearchStore(s => s.viewMode);
  const setViewMode = useSearchStore(s => s.setViewMode);
  const { focusShopOnMap } = useMapActions();
  const navigate = useNavigate();

  const [inputValue, setInputValue] = useState('');
  const [acOpen, setAcOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const acRef = useRef<SearchAutocompleteHandle>(null);

  // keep input mirrored to store query (handles logo reset -> clears input)
  useEffect(() => {
    setInputValue(storeQuery ?? '');
  }, [storeQuery]);

  const doFullSearch = useCallback((q: string) => {
    setAcOpen(false);
    onSearch?.(q);
    setInputValue(q);
    setQuery(q);
    if (typeof runSearchWithQuery === 'function') {
      runSearchWithQuery(q);
    } else {
      queueMicrotask(() => runSearch());
    }
    // Keep current view if user is already on map or list; only switch from hero
    if (viewMode === 'hero') setViewMode('list');
  }, [onSearch, setQuery, runSearch, runSearchWithQuery, viewMode, setViewMode]);

  const handleSelect = useCallback((hit: TypeaheadHit) => {
    setAcOpen(false);
    setInputValue(hit.name);
    if (viewMode === 'map') {
      // User is browsing the map — fly to the shop there instead of
      // yanking them away to the details page.
      focusShopOnMap({ id: hit.id, name: hit.name });
    } else {
      navigate(`/shop/${hit.id}`);
    }
  }, [navigate, viewMode, focusShopOnMap]);

  const handleSearchAll = useCallback((q: string) => {
    doFullSearch(q);
  }, [doFullSearch]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    doFullSearch(trimmed);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (e.target.value.trim().length >= 2) {
      setAcOpen(true);
    } else {
      setAcOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!acOpen) return;
    const maxIdx = (acRef.current?.itemCount ?? 7) - 1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => Math.min(prev + 1, maxIdx));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Escape') {
      setAcOpen(false);
      setActiveIndex(-1);
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      acRef.current?.selectActive();
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={`relative flex items-center w-full max-w-[680px] min-w-[280px] ${className || ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <form
        onSubmit={onSubmit}
        role="search"
        aria-label="Search coffee shops and roasters"
        autoComplete="off"
        noValidate
        className="flex items-center w-full gap-2"
      >
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => inputValue.trim().length >= 2 && setAcOpen(true)}
            onKeyDown={handleKeyDown}
            onBlur={() => setTimeout(() => setAcOpen(false), 200)}
            placeholder="Search, Sip, Repeat"
            autoComplete="off"
            aria-label="Search by name, city, state, or ZIP"
            data-form-type="other"
            name="gr-search"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            role="combobox"
            aria-expanded={acOpen}
            aria-autocomplete="list"
            className="w-full pr-10"
          />
          {inputValue && !acOpen && (
            <IconButton
              aria-label="clear search"
              onClick={() => {
                setAcOpen(false);
                resetSearch();
                navigate('/');
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-[2]"
            >
              <X className="w-4 h-4" />
            </IconButton>
          )}
          <SearchAutocomplete
            ref={acRef}
            inputValue={inputValue}
            onSelect={handleSelect}
            onSearchAll={handleSearchAll}
            anchorEl={inputRef.current}
            open={acOpen}
            onClose={() => setAcOpen(false)}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
          />
        </div>
        <Button type="submit">
          Search
        </Button>
      </form>
    </div>
  );
};

export default memo(SearchBarComponent);

